import React, { useState } from 'react';
import type { Row, Task } from '../types';
import { ROW_HEIGHT } from '../constants';
import { TaskBar } from './TaskBar';
import { useGanttContext } from '../GanttContext';

type Props = {
  row: Row;
  tasks: Task[];
  moveTaskDates: (taskId: string, startDate: string, endDate: string) => void;
  moveTaskToRow: (taskId: string, targetRowId: string) => void;
  createRow: () => { id: string };
  onPointClick: (taskId: string, side: 'start' | 'end', cx: number, cy: number) => void;
  onEditTask: (taskId: string) => void;
  hasUnresolvedDepsMap: Map<string, boolean>;
  
  // New handlers for row actions
  onAddRowAbove: (rowId: string) => void;
  onAddRowBelow: (rowId: string) => void;
  onSplitRow: (rowId: string) => void;
};

const rowBgX = (colW: number) => `repeating-linear-gradient(
  90deg,
  transparent,
  transparent ${colW - 1}px,
  #efefef ${colW - 1}px,
  #efefef ${colW}px
)`;

export const GanttRow: React.FC<Props> = ({
  row, 
  tasks, 
  moveTaskDates, 
  moveTaskToRow, 
  createRow, 
  onPointClick, 
  onEditTask, 
  hasUnresolvedDepsMap,
  onAddRowAbove,
  onAddRowBelow,
  onSplitRow
}) => {
  const ctx = useGanttContext();
  const rowBg = rowBgX(ctx.columnWidth);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      data-row-id={row.id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        height: ROW_HEIGHT,
        width: ctx.columnWidth * ctx.timelineDays,
        borderBottom: '1px solid #e8e8e8',
        backgroundImage: rowBg,
        backgroundSize: `${ctx.columnWidth}px 100%`,
        boxSizing: 'border-box',
        flexShrink: 0,
        // Using flex allows us to place sticky items easily next to absolute task bars
        display: 'flex', 
      }}
    >
      {/* Action Buttons container (Sticky to the left) */}
      <div 
        style={{
          position: 'sticky',
          left: 0,
          top: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '0 8px',
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(2px)',
          borderRight: '1px solid #ddd',
          zIndex: 30, // Above task bars
          boxShadow: '2px 0 5px rgba(0,0,0,0.05)',
          opacity: isHovered ? 1 : 0, // Hide using opacity instead of unmounting so sticky flow stays stable
          pointerEvents: isHovered ? 'auto' : 'none',
          transition: 'opacity 0.15s ease'
        }}
      >
        <button 
          onClick={() => onAddRowAbove(row.id)}
          title="Add row above"
          style={actionBtnStyle}
        >
          ↑
        </button>
        
        {tasks.length > 1 && (
          <button 
            onClick={() => onSplitRow(row.id)}
            title="Split tasks into separate rows"
            style={actionBtnStyle}
          >
            ⑆
          </button>
        )}

        <button 
          onClick={() => onAddRowBelow(row.id)}
          title="Add row below"
          style={actionBtnStyle}
        >
          ↓
        </button>
      </div>

      {tasks.map(task => (
        <TaskBar
          key={task.id}
          task={task}
          rowId={row.id}
          moveTaskDates={moveTaskDates}
          moveTaskToRow={moveTaskToRow}
          createRow={createRow}
          onPointClick={onPointClick}
          onEditTask={onEditTask}
          hasUnresolvedDependencies={hasUnresolvedDepsMap.get(task.id)}
        />
      ))}
    </div>
  );
};

const actionBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '12px',
  color: '#555',
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1
};
